import {
    useMutation,
    useQuery,
    useQueryClient,
    UseQueryResult,
} from '@tanstack/react-query';
import { HttpError, RQAPICallWithData } from 'store/APIClient';
import { useDispatch } from 'store/hooks';
import {
    KeyDate,
    KeyDateByExercice,
    KeyDateWithMetadata,
} from '../models/KeyDate';
import { BUDGET_KEY_DATES_EVENT } from '../components/BudgetKeyDates/constants';
import { groupBy } from 'lodash';

export const useGetBudgetKeyDates = (): UseQueryResult<
    KeyDateByExercice,
    HttpError
> =>
    useQuery({
        queryKey: ['budget_key_dates'],

        queryFn: async () => {
            /**
             * Actually the endpoint returns KeyDate[], but we augment it with
             * metadata (edited) to be able to use it in the UI.
             * TODO: We probably should avoid this `edited`.
             */
            const response = await RQAPICallWithData<KeyDateWithMetadata[]>({
                endpoint: `budget_overview/key_dates`,
                method: 'GET',
            });
            // we make sure that the order of key dates are always the same
            return response.content.data.sort((a, b) => {
                return (
                    BUDGET_KEY_DATES_EVENT.indexOf(a.event) -
                    BUDGET_KEY_DATES_EVENT.indexOf(b.event)
                );
            });
        },
        select: (data) => {
            const keyDatesWithEdited = data.map((keyDate) => ({
                ...keyDate,
                edited: keyDate.edited ?? false,
            }));
            return groupBy(keyDatesWithEdited, 'exercice');
        },
    });

const updateKeyDate = async (
    exercice: number,
    event: string,
    scheduledAt?: string,
) => {
    const body = scheduledAt === '' ? { scheduledAt: null } : { scheduledAt };
    const response = await RQAPICallWithData({
        endpoint: `budget_overview/key_dates/${exercice}/${event}`,
        method: 'PUT',
        body,
    });

    return response.content;
};

export const useUpdateKeyDate = () => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ exercice, event, scheduledAt }: KeyDate) =>
            updateKeyDate(exercice, event, scheduledAt),
        onSuccess: (content, updatedKeyDate) => {
            queryClient.setQueryData(
                ['budget_key_dates'],
                (keyDates: KeyDate[]) => {
                    return keyDates.map((keyDate) =>
                        keyDate.event === updatedKeyDate.event &&
                        keyDate.exercice === updatedKeyDate.exercice
                            ? updatedKeyDate
                            : keyDate,
                    );
                },
            );
            dispatch.vision.notifyUser({
                message: content.message,
                snackType: 'success',
            });
        },
    });
};
